/**
 * React Query Client Configuration
 *
 * Centralized QueryClient configuration with optimized defaults for:
 * - Caching strategy (stale-while-revalidate)
 * - Retry logic with exponential backoff
 * - Background refetching
 * - Error handling
 * - Performance optimization
 */

import { QueryClient, DefaultOptions } from '@tanstack/react-query';

/**
 * Default options for all queries and mutations
 */
const queryConfig: DefaultOptions = {
  queries: {
    // Stale-while-revalidate pattern
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 mins
    gcTime: 10 * 60 * 1000, // 10 minutes - keep unused data in cache for 10 mins (formerly cacheTime)

    // Retry configuration with exponential backoff
    retry: (failureCount, error: any) => {
      // Don't retry on 4xx errors (client errors)
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      // Retry up to 3 times for 5xx errors (server errors)
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

    // Background refetching
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    refetchOnReconnect: true, // Refetch when internet reconnects
    refetchOnMount: true, // Refetch when component mounts

    // Performance
    networkMode: 'online', // Only run queries when online

    // Error handling
    throwOnError: false, // Don't throw errors, let components handle them
  },

  mutations: {
    // Retry failed mutations once (e.g., for network blips)
    retry: (failureCount, error: any) => {
      // Don't retry on 4xx errors (validation errors, etc)
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      // Retry once for 5xx errors
      return failureCount < 1;
    },
    retryDelay: 1000,

    // Error handling
    throwOnError: false,

    // Network mode
    networkMode: 'online',
  },
};

/**
 * Create and configure the QueryClient instance
 *
 * @returns {QueryClient} Configured QueryClient instance
 */
export const createQueryClient = (): QueryClient => {
  return new QueryClient({
    defaultOptions: queryConfig,
  });
};

/**
 * Singleton QueryClient instance
 *
 * Use this in your app's root provider.
 * For testing, create a new instance with `createQueryClient()`.
 */
export const queryClient = createQueryClient();

/**
 * Query key prefixes for better organization and invalidation
 */
export const QUERY_KEY_PREFIXES = {
  DASHBOARD: 'dashboard',
  FINANCIAL_EVENTS: 'financial-events',
  COHOST_FINANCIALS: 'cohost-financials',
  VEHICLES: 'vehicles',
  TRIPS: 'trips',
  EXPENSES: 'expenses',
  PAYMENTS: 'payments',
  AGREEMENTS: 'agreements',
  COHOSTS: 'cohosts',
  ANALYTICS: 'analytics',
  SUBSCRIPTION: 'subscription',
  USER: 'user',
  ACCOUNT: 'account',
  TEAM: 'team',
  LIFECYCLE: 'lifecycle',
} as const;

/**
 * Helper to invalidate all queries for a specific prefix
 * 
 * CRITICAL: Query keys are now account-scoped, so the prefix is at position 1:
 * [accountId, prefix, ...rest] - e.g., ['acc_123', 'vehicles', 'list', filters]
 * 
 * For unscoped keys (user, account), prefix is at position 0:
 * [prefix, ...rest] - e.g., ['user', 'profile']
 *
 * @example
 * ```ts
 * // Invalidate all cohost financial queries
 * invalidateQueriesByPrefix(queryClient, 'cohost-financials');
 * ```
 */
export const invalidateQueriesByPrefix = async (
  client: QueryClient,
  prefix: string
): Promise<void> => {
  await client.invalidateQueries({
    predicate: (query) => {
      const queryKey = query.queryKey;
      if (!Array.isArray(queryKey)) return false;
      // Check both position 0 (unscoped) and position 1 (account-scoped)
      return queryKey[0] === prefix || queryKey[1] === prefix;
    },
  });
};

/**
 * Helper to remove all queries for a specific prefix
 * Useful when you want to clear cache completely
 * 
 * Handles both account-scoped keys (prefix at position 1) and
 * unscoped keys (prefix at position 0).
 */
export const removeQueriesByPrefix = (
  client: QueryClient,
  prefix: string
): void => {
  client.removeQueries({
    predicate: (query) => {
      const queryKey = query.queryKey;
      if (!Array.isArray(queryKey)) return false;
      // Check both position 0 (unscoped) and position 1 (account-scoped)
      return queryKey[0] === prefix || queryKey[1] === prefix;
    },
  });
};

/**
 * Invalidate ALL queries in the cache
 * 
 * Used when switching accounts to ensure all data is refetched
 * with the new account context. This is cleaner than a full page reload.
 * 
 * @example
 * ```ts
 * // After account switch
 * invalidateAllQueries();
 * ```
 */
export const invalidateAllQueries = async (): Promise<void> => {
  await queryClient.invalidateQueries();
};

/**
 * Clear ALL queries from the cache
 * 
 * Used on logout to ensure no stale data remains.
 * More aggressive than invalidateAllQueries - removes data entirely.
 */
export const clearAllQueries = (): void => {
  queryClient.clear();
};
